import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Play, Pause, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const SS = {
  hero: '/screenshots/screenshot-11.png',
  dashboard: '/screenshots/screenshot-12.png',
  claims: '/screenshots/screenshot-13.png',
  coding: '/screenshots/screenshot-14.png',
  denials: '/screenshots/screenshot-15.png',
  pipeline: '/screenshots/screenshot-16.png',
  analytics: '/screenshots/screenshot-17.png',
  eligibility: '/screenshots/screenshot-18.png',
  payments: '/screenshots/screenshot-19.png',
  fullbleed: '/screenshots/screenshot-20.png',
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
};
const fadeUpHeading = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const slides = [
  { eyebrow: 'Executive Dashboard', title: 'Revenue at a glance.', body: 'Net collections, denial rates, days in A/R, and claim pipeline — all in one view with daily billing workflow steps.', img: SS.hero },
  { eyebrow: 'Claims Pipeline', title: 'Every claim, tracked.', body: 'From draft to paid — see claim status across all stages. Color-coded pipeline shows exactly where every dollar sits.', img: SS.pipeline },
  { eyebrow: 'AI Coding', title: 'Coded before you hit submit.', body: 'ICD-10 and CPT codes applied from the signed SOAP note automatically. AI scrubs for accuracy before submission.', img: SS.coding },
  { eyebrow: 'Denial Management', title: 'Appeals that write themselves.', body: 'RevQ identifies the denial reason, generates the appeal letter, and resubmits — without staff involvement.', img: SS.denials },
];

const stickyBlocks = [
  { eyebrow: 'After the encounter', h3: 'The claim builds itself.', body: 'The moment Lumina\'s SOAP note is signed, RevQ reads the documentation, applies ICD-10 and CPT codes, and generates a clean claim — no manual coding required.', pill: '98%+ coding accuracy', img: SS.hero },
  { eyebrow: 'Before submission', h3: 'Every rule checked automatically.', body: 'Payer-specific edits, modifier validation, eligibility verification, and prior authorization status — all validated before the claim leaves your practice.', pill: '97.2% clean claim rate', img: SS.claims },
  { eyebrow: 'After submission', h3: 'Denials caught. Appeals filed.', body: 'When a denial comes back, RevQ identifies the root cause, generates a targeted appeal with supporting documentation, and resubmits automatically.', pill: '40% fewer denials', img: SS.analytics },
  { eyebrow: 'Every day', h3: 'Every dollar, visible.', body: 'Real-time dashboards show net collections, payer performance, denial patterns, and days in A/R. Know exactly where your revenue stands — not 30 days later.', pill: '12 days faster reimbursement', img: SS.payments },
];

const featureCards = [
  { name: 'AI Claim Generation', desc: 'Reads signed SOAP notes and generates clean CMS-1500 claims automatically.', img: SS.hero },
  { name: 'AI Coding Review', desc: 'ICD-10 and CPT codes validated with confidence scores and audit-ready accuracy.', img: SS.coding },
  { name: 'Denial Management', desc: 'Identifies denial patterns, generates appeals, and tracks resubmission status.', img: SS.denials },
  { name: 'Eligibility Verification', desc: 'Real-time insurance eligibility checks before the patient arrives.', img: SS.eligibility },
  { name: 'Payment Posting', desc: 'ERA/EOB processing with automatic payment posting and reconciliation.', img: SS.payments },
  { name: 'Revenue Analytics', desc: 'Payer performance, denial rates, collection trends, and days in A/R dashboards.', img: SS.analytics },
];

const HeroSection = () => {
  const mockupRef = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 180], [72, 0]);
  const rotX = useTransform(scrollY, [0, 180], [6, 0]);
  const sc = useTransform(scrollY, [0, 180], [0.97, 1]);
  const op = useTransform(scrollY, [0, 180], [0.75, 1]);

  return (
    <section className="relative pt-32 pb-0 white-texture" style={{ background: '#0a0a0a' }} data-testid="revq-hero">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-[680px] mx-auto px-4 text-center">
        <motion.p variants={fadeUp} className="text-sm font-medium tracking-[0.1em] uppercase" style={{ color: '#888' }}>Billing Intelligence</motion.p>
        <motion.h1 variants={fadeUpHeading} className="text-[40px] md:text-[56px] lg:text-[72px] text-white font-bold leading-[1.1] tracking-[-0.03em] mt-4">
          Every claim. Clean. On time.
        </motion.h1>
        <motion.p variants={fadeUp} className="text-lg text-[#aaaaaa] leading-[1.6] max-w-[560px] mx-auto mt-4 font-normal">
          RevQ automates the entire revenue cycle — from AI-powered coding to claim submission, denial management, and payment posting. Fewer denials, faster cash, zero manual coding.
        </motion.p>
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mt-8">
          <Link to="/contact" className="h-11 px-6 rounded-lg text-black text-sm font-medium flex items-center gap-2" style={{ background: '#ffffff' }} data-testid="revq-cta-trial">
            Start free trial
          </Link>
          <button className="h-11 px-6 rounded-lg text-white text-sm font-medium flex items-center gap-2" style={{ border: '0.5px solid #2a2a2a' }} data-testid="revq-cta-demo">
            See it in action
          </button>
        </motion.div>
      </motion.div>
      <div className="relative mt-14 flex justify-center" style={{ perspective: '1200px' }}>
        <motion.div ref={mockupRef} style={{ y, rotateX: rotX, scale: sc, opacity: op, willChange: 'transform, opacity' }} className="w-[88vw] max-w-[1080px]">
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="h-10 flex items-center px-3.5" style={{ background: '#242424', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
              </div>
              <div className="mx-auto h-[26px] px-4 rounded-md flex items-center" style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="text-[#555] text-xs font-mono">app.ariaone.ai/revq</span>
              </div>
            </div>
            <img src={SS.hero} alt="RevQ Executive Dashboard" className="w-full object-cover object-top" style={{ height: 580 }} />
          </div>
          <div className="absolute bottom-[-20px] left-1/2 -translate-x-1/2 w-[60%] h-[80px] rounded-full" style={{ background: 'rgba(107,92,231,0.1)', filter: 'blur(60px)' }} />
        </motion.div>
      </div>
    </section>
  );
};

const stats = [
  { num: '97.2%', label: 'clean claim rate' },
  { num: '40%', label: 'fewer denials' },
  { num: '12 days', label: 'faster reimbursement' },
  { num: '$0', label: 'missed revenue with AI coding' },
];

const StatBand = () => (
  <section className="w-full py-10" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a', borderBottom: '0.5px solid #2a2a2a' }} data-testid="revq-stat-band">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[1080px] mx-auto px-4 flex items-center justify-between">
      {stats.map((s, i) => (
        <div key={i} className="flex items-center">
          {i > 0 && <div className="h-10 mr-8 lg:mr-12" style={{ width: '0.5px', background: '#2a2a2a' }} />}
          <motion.div variants={fadeUp} className="text-center flex-1">
            <div className="text-white text-3xl md:text-[40px] font-bold">{s.num}</div>
            <div className="text-[#555] text-xs mt-1">{s.label}</div>
          </motion.div>
        </div>
      ))}
    </motion.div>
  </section>
);

const HighlightCarousel = () => {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);
  const advance = useCallback(() => setActive(p => (p + 1) % slides.length), []);

  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(advance, 5000);
    return () => clearInterval(timerRef.current);
  }, [playing, advance]);

  return (
    <section className="py-20 md:py-28 white-texture" style={{ background: '#0a0a0a' }} data-testid="revq-carousel">
      <div className="max-w-[1200px] mx-auto px-4">
        <h2 className="text-[32px] text-white font-bold mb-8">Get the highlights</h2>
      </div>
      <div className="flex justify-center">
        <div className="w-[88vw] max-w-[1200px] rounded-3xl overflow-hidden relative" style={{ background: '#111111', height: 520 }} onMouseEnter={() => setPlaying(false)} onMouseLeave={() => setPlaying(true)}>
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="absolute inset-0 flex">
              <div className="w-[38%] p-12 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>{slides[active].eyebrow}</p>
                <h3 className="text-[28px] text-white font-bold leading-[1.2] mt-3">{slides[active].title}</h3>
                <p className="text-[#aaa] text-base leading-[1.7] mt-3 max-w-[300px] font-normal">{slides[active].body}</p>
              </div>
              <div className="w-[62%] relative">
                <img src={slides[active].img} alt={slides[active].title} className="absolute inset-0 w-full h-full object-cover object-top" />
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="absolute bottom-5 left-12 flex items-center gap-3 z-10">
            <button onClick={() => setPlaying(p => !p)} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.08)' }} data-testid="revq-carousel-play">
              {playing ? <Pause className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white" />}
            </button>
            <div className="flex gap-1.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setActive(i)} className="h-1 rounded-full overflow-hidden" style={{ width: 40, background: 'rgba(255,255,255,0.1)' }}>
                  <motion.div className="h-full rounded-full" style={{ background: '#ffffff' }} initial={{ width: 0 }} animate={{ width: i === active && playing ? '100%' : i === active ? '100%' : '0%' }} transition={{ duration: i === active && playing ? 5 : 0.2, ease: 'linear' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StickyScrollSection = () => {
  const [activeBlock, setActiveBlock] = useState(0);
  const blockRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setActiveBlock(Number(entry.target.dataset.idx));
      });
    }, { threshold: 0.4 });
    blockRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="white-texture" style={{ background: '#0a0a0a' }} data-testid="revq-sticky-scroll">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[640px] mx-auto text-center px-4 pb-20">
        <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>How it works</motion.p>
        <motion.h2 variants={fadeUpHeading} className="text-3xl md:text-[48px] text-white font-bold tracking-tight leading-[1.15] mt-4">From signed note to paid claim — without touching a spreadsheet.</motion.h2>
        <motion.p variants={fadeUp} className="text-[17px] text-[#aaa] mt-4 font-normal">RevQ handles the entire revenue cycle automatically. Here's what that looks like.</motion.p>
      </motion.div>
      <div className="relative" style={{ minHeight: '500vh' }}>
        <div className="flex">
          <div className="w-[45%] px-8 md:px-14">
            {stickyBlocks.map((block, i) => (
              <div key={i} ref={el => (blockRefs.current[i] = el)} data-idx={i} className="py-20 md:py-28" style={{ minHeight: '100vh' }}>
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                  <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>{block.eyebrow}</motion.p>
                  <motion.h3 variants={fadeUpHeading} className="text-2xl md:text-3xl text-white font-bold tracking-tight mt-3">{block.h3}</motion.h3>
                  <motion.p variants={fadeUp} className="text-[17px] text-[#aaa] leading-[1.7] mt-4 max-w-[420px] font-normal">{block.body}</motion.p>
                  <motion.div variants={fadeUp} className="inline-block mt-4 px-3.5 py-1 rounded-full text-[13px]" style={{ background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.15)', color: '#aaa' }}>{block.pill}</motion.div>
                </motion.div>
              </div>
            ))}
          </div>
          <div className="w-[55%] sticky top-0 h-screen flex items-center p-10">
            <div className="w-full rounded-2xl overflow-hidden" style={{ background: '#111111', border: '0.5px solid #2a2a2a', height: 'calc(100vh - 80px)' }}>
              <AnimatePresence mode="wait">
                <motion.img key={activeBlock} src={stickyBlocks[activeBlock].img} alt={stickyBlocks[activeBlock].h3} className="w-full h-full object-cover object-top rounded-2xl" initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} />
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FullWidthBreak = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
  return (
    <section ref={ref} className="relative w-full overflow-hidden" style={{ height: '60vh' }} data-testid="revq-fullwidth-break">
      <motion.img src={SS.fullbleed} alt="RevQ Revenue Dashboard" className="absolute inset-0 w-full h-[120%] object-cover object-top" style={{ y: imgY }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 65%, #0a0a0a 100%)' }} />
    </section>
  );
};

const CodingEditorialSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);
  const checks = ['ICD-10 and CPT codes from signed notes', 'Confidence scores for every code applied', 'Payer-specific modifier validation'];
  return (
    <section ref={ref} className="w-full" style={{ background: '#0a0a0a' }} data-testid="revq-coding-editorial">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        <motion.div className="w-full lg:w-1/2 px-8 md:px-16 lg:px-20 flex flex-col justify-center py-16" initial={{ opacity: 0, x: -32 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>AI Coding Intelligence</p>
          <h2 className="text-3xl md:text-[48px] text-white font-bold leading-[1.15] tracking-tight max-w-[460px] mt-4">Coded in seconds. Audit-ready always.</h2>
          <p className="text-[17px] text-[#aaa] leading-[1.7] max-w-[420px] mt-4 font-normal">
            RevQ reads the signed SOAP note and applies the most accurate ICD-10 and CPT codes — with confidence scores so you know exactly how certain each code is.
          </p>
          <div className="mt-5 space-y-2.5">
            {checks.map((c, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#888' }} />
                <span className="text-[#aaa] text-sm font-normal">{c}</span>
              </div>
            ))}
          </div>
          <Link to="/contact" className="text-white text-[15px] mt-6 inline-flex items-center gap-1.5 hover:underline">See AI Coding in action <ArrowRight className="w-4 h-4" /></Link>
        </motion.div>
        <motion.div className="w-full lg:w-1/2 relative overflow-hidden min-h-[400px]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <motion.img src={SS.coding} alt="AI Coding" className="absolute inset-0 w-full h-full object-cover object-top" style={{ y: imgY }} />
        </motion.div>
      </div>
    </section>
  );
};

const FeatureGrid = () => (
  <section className="py-20 md:py-28 white-texture" style={{ background: '#111111' }} data-testid="revq-feature-grid">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[1200px] mx-auto px-4">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Everything in RevQ</p>
        <h2 className="text-3xl md:text-[48px] text-white font-bold tracking-tight mt-3">Built for the full revenue cycle.</h2>
        <p className="text-[#aaa] text-base mt-3 font-normal">From coding to collections — every step automated.</p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {featureCards.map((card, i) => (
          <motion.div key={i} variants={fadeUp} className="rounded-2xl p-7 transition-colors duration-200 group" style={{ background: '#111111', border: '0.5px solid #2a2a2a' }} whileHover={{ borderColor: 'rgba(255,255,255,0.3)' }} data-testid={`revq-feature-card-${i}`}>
            <div className="h-20 rounded-lg overflow-hidden mb-4" style={{ background: '#1a1a1a' }}>
              <img src={card.img} alt={card.name} className="w-full h-full object-cover object-top" />
            </div>
            <h3 className="text-white text-lg font-bold mt-2">{card.name}</h3>
            <p className="text-[#aaa] text-sm leading-[1.6] mt-2 font-normal">{card.desc}</p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  </section>
);

const DenialEditorialSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);
  return (
    <section ref={ref} className="w-full" style={{ background: '#0a0a0a' }} data-testid="revq-denial-editorial">
      <div className="flex flex-col lg:flex-row min-h-[600px]">
        <motion.div className="w-full lg:w-1/2 relative overflow-hidden min-h-[400px] order-2 lg:order-1" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <motion.img src={SS.denials} alt="Denial Management" className="absolute inset-0 w-full h-full object-cover object-top" style={{ y: imgY }} />
        </motion.div>
        <motion.div className="w-full lg:w-1/2 px-8 md:px-16 lg:px-20 flex flex-col justify-center py-16 order-1 lg:order-2" initial={{ opacity: 0, x: 32 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <p className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Denial Management</p>
          <h2 className="text-3xl md:text-[48px] text-white font-bold leading-[1.15] tracking-tight max-w-[460px] mt-4">Denials don't stand a chance.</h2>
          <p className="text-[17px] text-[#aaa] leading-[1.7] max-w-[420px] mt-4 font-normal">
            RevQ identifies denial patterns, generates targeted appeal letters with supporting documentation, and tracks every resubmission — automatically reducing your denial rate over time.
          </p>
          <Link to="/contact" className="text-white text-[15px] mt-6 inline-flex items-center gap-1.5 hover:underline">Explore Denial Management <ArrowRight className="w-4 h-4" /></Link>
        </motion.div>
      </div>
    </section>
  );
};

const PricingSection = () => (
  <section className="py-20 md:py-28 white-texture" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a' }} data-testid="revq-pricing">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[900px] mx-auto px-4 text-center">
      <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.08em] font-medium" style={{ color: '#888' }}>Flexible Usage Model</motion.p>
      <motion.h2 variants={fadeUpHeading} className="text-2xl md:text-3xl text-white font-bold tracking-tight mt-3">Pay only for what you use</motion.h2>
      <motion.p variants={fadeUp} className="text-[#aaa] text-base mt-3 max-w-[500px] mx-auto font-normal">Credits-based pricing. No long-term contracts required.</motion.p>
      <motion.div variants={fadeUp} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
        {[
          { name: 'Lumina', credits: '50–100', desc: 'Per encounter. Ambient capture, SOAP note, and coding review.' },
          { name: 'RevQ', credits: '30–80', desc: 'Per claim. AI scrubbing, submission, denial management.' },
          { name: 'Echo', credits: '20–50', desc: 'Per scheduling action. Smart scheduling, AI phone, patient flow.' },
        ].map((p, i) => (
          <div key={i} className="rounded-xl p-6" style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a' }}>
            <div className="flex items-center gap-2 mb-3">
              <img src={`/screenshots/${p.name.toLowerCase()}_emblem_white.png`} alt={p.name} className="w-6 h-6 object-contain" />
              <span className="text-white text-base font-bold">{p.name}</span>
            </div>
            <div className="text-2xl text-white font-bold">{p.credits} <span className="text-sm font-normal text-[#555]">credits</span></div>
            <p className="text-[#aaa] text-sm mt-2 font-normal leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </motion.div>
    </motion.div>
  </section>
);

const EcosystemBand = () => (
  <section className="py-14" style={{ background: '#0a0a0a', borderTop: '0.5px solid #2a2a2a' }} data-testid="revq-ecosystem">
    <div className="max-w-[600px] mx-auto px-4 text-center">
      <p className="text-[#555] text-xs uppercase tracking-[0.08em]">Part of the Aria One platform</p>
      <div className="flex items-center justify-center gap-12 mt-5">
        {['lumina', 'revq', 'echo'].map(name => (
          <img key={name} src={`/screenshots/${name}_logo_white.png`} alt={name} className="h-6 object-contain" style={{ opacity: 0.6 }} />
        ))}
      </div>
      <p className="text-[#555] text-sm mt-4 max-w-[500px] mx-auto font-normal">
        Lumina signs the note. RevQ codes and bills it. Echo schedules the follow-up. One seamless revenue cycle.
      </p>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="py-24 white-texture" style={{ background: '#111111', borderTop: '0.5px solid #2a2a2a' }} data-testid="revq-final-cta">
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-[640px] mx-auto px-4 text-center">
      <motion.h2 variants={fadeUpHeading} className="text-3xl md:text-[40px] text-white font-bold tracking-tight">Ready to stop leaving money on the table?</motion.h2>
      <motion.p variants={fadeUp} className="text-[#aaa] text-base mt-3 font-normal">Start with RevQ today. No long-term contract. Cancel anytime.</motion.p>
      <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mt-8">
        <Link to="/contact" className="h-11 px-6 rounded-lg text-black text-sm font-medium flex items-center" style={{ background: '#ffffff' }}>Start free trial</Link>
        <button className="h-11 px-6 rounded-lg text-white text-sm font-medium flex items-center" style={{ border: '0.5px solid #2a2a2a' }}>Request a demo</button>
      </motion.div>
    </motion.div>
  </section>
);

const RevQPage = () => (
  <div style={{ background: '#0a0a0a' }}>
    <HeroSection />
    <StatBand />
    <HighlightCarousel />
    <StickyScrollSection />
    <FullWidthBreak />
    <CodingEditorialSection />
    <FeatureGrid />
    <DenialEditorialSection />
    <PricingSection />
    <EcosystemBand />
    <FinalCTA />
  </div>
);

export default RevQPage;
